import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "@/api/types";
import { actionKey, apiPost, qk, resetActionKey } from "@/shared/api";

type DepositCreate = components["schemas"]["DepositCreate"];

/**
 * Заявка на пополнение.
 *
 * Баланс здесь не двигается: заявка только создаёт платёж в шлюзе, а зачисляет
 * колбэк, когда деньги придут. Поэтому `wallet` инвалидировать незачем —
 * экран заявки сам опрашивает статус и обновит кошелёк, когда тот изменится.
 * Событие в ленте появляется сразу.
 *
 * Ключ идемпотентности привязан к действию, а не к попытке отправки: иначе
 * повтор после таймаута создал бы вторую заявку на те же деньги.
 */
const ACTION = "deposit";

export function useCreateDeposit() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (payload: DepositCreate) =>
      apiPost("/api/v1/wallet/deposit", { body: payload, idempotencyKey: actionKey(ACTION) }),
    onSuccess: (request) => {
      resetActionKey(ACTION);
      // Ответ на создание — это полный объект заявки. Кладём его в кэш сразу,
      // чтобы экран статуса открылся без лишнего запроса.
      client.setQueryData(qk.depositRequest(request.id), request);
      void client.invalidateQueries({ queryKey: ["activity"] });
    },
  });
}
